import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Read-only reminder status for one payday. Purely informational — it shows
 * whether the two automatic reminder emails have gone out. It approves
 * nothing, pays nothing and changes no payroll data.
 */
export function PayrollReminderStatus({ payDate }: { payDate?: string | null }) {
  const { data } = useQuery({
    queryKey: ["payroll", "reminders", payDate],
    enabled: !!payDate,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payroll_reminder_sends")
        .select("reminder_type, status, sent_at")
        .eq("pay_date", payDate!)
        .eq("status", "sent");
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!payDate) return null;

  const sentAt = (type: string) =>
    data?.find((r) => r.reminder_type === type)?.sent_at ?? null;

  const item = (label: string, at: string | null) => (
    <span className="inline-flex items-center gap-1">
      <span
        aria-hidden
        className={`inline-block h-1.5 w-1.5 rounded-full ${at ? "bg-primary" : "bg-muted-foreground/40"}`}
      />
      {label}:{" "}
      {at
        ? new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : "not sent"}
    </span>
  );

  return (
    <p className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      <span>Reminder emails</span>
      {item("Finalization", sentAt("finalize"))}
      {item("Payday", sentAt("payday"))}
    </p>
  );
}
